import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Button, SoftButton } from 'components/Button';
import Loading from 'components/Loading';
import {
    useFetchCountries,
    useFetchUserAbout,
    useSetPreferredCountry,
} from 'utils/hooks';
import ExtLink from 'components/ExtLink';
import FilterCheckbox from 'components/FilterAccordion/FilterCheckbox';
import {
    Card,
    CardContent,
    CardHeader,
    CardTitle,
} from '@/ui/design-system/components/ui/card';

const MarketGrid = styled.fieldset`
    all: unset;
    display: grid;
    gap: ${({ theme }) => theme.space_5}px;

    @media screen and (min-width: ${({ theme }) => theme.bp.sm}px) {
        grid-template-columns: 1fr 1fr;
    }
`;

function General() {
    const countriesQuery = useFetchCountries();
    const aboutQuery = useFetchUserAbout();
    const setPreferredCountry = useSetPreferredCountry();
    const [selectedCountry, setSelectedCountry] = useState(null);

    useEffect(() => {
        if (aboutQuery.data?.preferred_country && !selectedCountry) {
            setSelectedCountry(aboutQuery.data.preferred_country);
        }
    }, [aboutQuery.data, selectedCountry]);

    const handleSubmit = (e) => {
        e.preventDefault();
        if (selectedCountry) {
            setPreferredCountry.mutate(selectedCountry);
        }
    };

    return (
        <form className="flex flex-col gap-6" onSubmit={handleSubmit}>
            <Card>
                <CardHeader>
                    <CardTitle className="text-sm font-medium">
                        Preferred Market
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    <MarketGrid>
                        {countriesQuery.data ? (
                            countriesQuery.data.map(({ id, name, text_id }) => {
                                const isSelected = selectedCountry === text_id;
                                const disabled =
                                    !aboutQuery.data?.allowed_countries?.includes(
                                        text_id,
                                    ) || setPreferredCountry.isLoading;

                                return (
                                    <FilterCheckbox
                                        key={id}
                                        checked={isSelected}
                                        disabled={disabled}
                                        onChange={() =>
                                            setSelectedCountry(text_id)
                                        }
                                    >
                                        {name}
                                    </FilterCheckbox>
                                );
                            })
                        ) : (
                            <div className="flex justify-center">
                                {countriesQuery.isLoading ? (
                                    <Loading />
                                ) : countriesQuery.isError ? (
                                    'Could not load markets'
                                ) : null}
                            </div>
                        )}
                    </MarketGrid>
                </CardContent>
            </Card>
            <Card>
                <CardHeader>
                    <CardTitle className="text-sm font-medium">
                        Cookies
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    <span className="text-sm">
                        Change your{' '}
                        <SoftButton
                            intent="secondary"
                            id="cookie-settings"
                            type="button"
                            disabled={!window.OneTrust}
                            onClick={() => OneTrust.ToggleInfoDisplay()}
                        >
                            Cookie Settings
                        </SoftButton>{' '}
                        for the Spate dashboard and{' '}
                        <ExtLink
                            href="https://www.spate.nyc/compliance/cookie-policy"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            learn more about cookies
                        </ExtLink>
                        .
                    </span>
                </CardContent>
            </Card>
            <Button
                disabled={
                    setPreferredCountry.isLoading ||
                    countriesQuery.isLoading ||
                    aboutQuery.isLoading
                }
                fetching={setPreferredCountry.isLoading}
                type="submit"
                css="align-self: center; min-width: 15.625rem;"
            >
                Apply Settings
            </Button>
        </form>
    );
}

export default General;
